import React, { useState, useEffect } from 'react';
import ApiClient, { Channel } from '../api/client';
import { ModalShell } from './ui';
import { AlertIcon } from './icons';

interface ChannelDialogProps {
  apiClient: ApiClient;
  channel?: Channel;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    type: 'slack' | 'pushover' | 'email' | 'convertkit' | 'webhook' | 'mautic' | 'twilio-sms' | 'espocrm' | 'pbx-crm';
    config: Record<string, any>;
    testOnCreate?: boolean;
  }) => void;
}

interface ConvertKitItem {
  id: number;
  name: string;
  type?: string;
}

const ChannelDialog: React.FC<ChannelDialogProps> = ({ apiClient, channel, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<'slack' | 'pushover' | 'email' | 'convertkit' | 'webhook' | 'mautic' | 'twilio-sms' | 'espocrm' | 'pbx-crm'>(
    'slack'
  );
  const [slackWebhook, setSlackWebhook] = useState('');
  const [pushoverToken, setPushoverToken] = useState('');
  const [pushoverUserKey, setPushoverUserKey] = useState('');
  const [pushoverPriority, setPushoverPriority] = useState('0');
  const [emailTo, setEmailTo] = useState('');
  const [convertkitApiKey, setConvertkitApiKey] = useState('');
  const [convertkitTags, setConvertkitTags] = useState<string[]>([]);
  const [convertkitAvailableTags, setConvertkitAvailableTags] = useState<ConvertKitItem[]>([]);
  const [loadingConvertkit, setLoadingConvertkit] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookHeaders, setWebhookHeaders] = useState('');
  const [webhookPayload, setWebhookPayload] = useState('');
  // Mautic state
  const [mauticHost, setMauticHost] = useState('');
  const [mauticUsername, setMauticUsername] = useState('');
  const [mauticPassword, setMauticPassword] = useState('');
  const [mauticSegmentId, setMauticSegmentId] = useState('');
  const [mauticTags, setMauticTags] = useState<string[]>([]);
  const [mauticNewTag, setMauticNewTag] = useState('');
  const [mauticAvailableSegments, setMauticAvailableSegments] = useState<Array<{ id: number; name: string }>>([]);
  const [mauticAvailableTags, setMauticAvailableTags] = useState<Array<{ id: number; tag: string }>>([]);
  const [mauticAvailableEmails, setMauticAvailableEmails] = useState<Array<{ id: number; name: string; subject: string; emailType: string; isPublished: boolean }>>([]);
  const [mauticAvailableCampaigns, setMauticAvailableCampaigns] = useState<Array<{ id: number; name: string; isPublished: boolean; events: Array<{ id: number; name: string; type: string; eventType: string; order: number; channelId: number | null }> }>>([]);
  const [mauticImmediateEmailId, setMauticImmediateEmailId] = useState('');
  const [mauticImmediateCampaignId, setMauticImmediateCampaignId] = useState('');
  const [mauticImmediateCampaignEventId, setMauticImmediateCampaignEventId] = useState('');
  const [mauticEnableTrackingMerge, setMauticEnableTrackingMerge] = useState(false);
  const [mauticConnectionTested, setMauticConnectionTested] = useState(false);
  const [loadingMautic, setLoadingMautic] = useState(false);

  // Twilio SMS state
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioFromNumber, setTwilioFromNumber] = useState('');
  const [twilioToNumbers, setTwilioToNumbers] = useState<string[]>([]);
  const [twilioNewToNumber, setTwilioNewToNumber] = useState('');
  const [twilioMessageTemplate, setTwilioMessageTemplate] = useState('');
  const [twilioAvailableNumbers, setTwilioAvailableNumbers] = useState<Array<{ phoneNumber: string; friendlyName: string }>>([]);
  const [twilioConnectionTested, setTwilioConnectionTested] = useState(false);
  const [loadingTwilio, setLoadingTwilio] = useState(false);

  // EspoCRM state
  const [espocrmHost, setEspocrmHost] = useState('');
  const [espocrmApiKey, setEspocrmApiKey] = useState('');
  const [espocrmTeamId, setEspocrmTeamId] = useState('');
  const [espocrmLeadSource, setEspocrmLeadSource] = useState('');
  const [espocrmLeadStatus, setEspocrmLeadStatus] = useState('New');
  const [espocrmFieldMap, setEspocrmFieldMap] = useState<Record<string, string>>({});
  const [espocrmNewFormField, setEspocrmNewFormField] = useState('');
  const [espocrmNewEspoField, setEspocrmNewEspoField] = useState('');
  const [espocrmAvailableTeams, setEspocrmAvailableTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [espocrmAvailableFields, setEspocrmAvailableFields] = useState<Array<{ name: string; label: string; type: string }>>([]);
  const [espocrmCreateAccount, setEspocrmCreateAccount] = useState(false);
  const [espocrmCreateOpportunity, setEspocrmCreateOpportunity] = useState(false);
  const [espocrmOppStage, setEspocrmOppStage] = useState('New Inquiry');
  const [espocrmOppProbability, setEspocrmOppProbability] = useState('10');
  const [espocrmOppCloseDate, setEspocrmOppCloseDate] = useState('+90days');
  const [espocrmOppFieldMap, setEspocrmOppFieldMap] = useState<Record<string, string>>({});
  const [espocrmOppNewFormField, setEspocrmOppNewFormField] = useState('');
  const [espocrmOppNewOppField, setEspocrmOppNewOppField] = useState('');
  const [espocrmConnectionTested, setEspocrmConnectionTested] = useState(false);
  const [loadingEspocrm, setLoadingEspocrm] = useState(false);

  // PBX option map state
  const [pbxOptionMap, setPbxOptionMap] = useState<Record<string, string>>({});
  const [pbxNewOptionKey, setPbxNewOptionKey] = useState('');
  const [pbxNewOptionLabel, setPbxNewOptionLabel] = useState('');

  const [testOnCreate, setTestOnCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (channel) {
      setName(channel.name);
      setType(channel.type);
      if (channel.config) {
        if (channel.type === 'slack' && channel.config.slackWebhook) {
          setSlackWebhook(channel.config.slackWebhook);
        } else if (channel.type === 'email' && channel.config.emailTo) {
          setEmailTo(Array.isArray(channel.config.emailTo) ? channel.config.emailTo.join('\n') : channel.config.emailTo);
        } else if (channel.type === 'pushover') {
          if (channel.config.pushoverToken) setPushoverToken(channel.config.pushoverToken);
          if (channel.config.pushoverUserKey) setPushoverUserKey(channel.config.pushoverUserKey);
          if (channel.config.priority !== undefined && channel.config.priority !== null) {
            setPushoverPriority(channel.config.priority.toString());
          }
        } else if (channel.type === 'convertkit') {
          if (channel.config.convertkitApiKey) setConvertkitApiKey(channel.config.convertkitApiKey);
          if (channel.config.convertkitTags) {
            const tags = Array.isArray(channel.config.convertkitTags)
              ? channel.config.convertkitTags.filter((t) => t != null).map((t) => t.toString())
              : [];
            setConvertkitTags(tags);
          }
        } else if (channel.type === 'webhook') {
          if (channel.config.url) setWebhookUrl(channel.config.url);
          if (channel.config.headers) setWebhookHeaders(JSON.stringify(channel.config.headers, null, 2));
          if (channel.config.payload) setWebhookPayload(JSON.stringify(channel.config.payload, null, 2));
        } else if (channel.type === 'mautic') {
          if (channel.config.mauticHost) setMauticHost(channel.config.mauticHost);
          if (channel.config.mauticUsername) setMauticUsername(channel.config.mauticUsername);
          if (channel.config.mauticPassword) setMauticPassword(channel.config.mauticPassword);
          if (channel.config.mauticSegmentId) setMauticSegmentId(channel.config.mauticSegmentId.toString());
          if (channel.config.mauticTags) setMauticTags(channel.config.mauticTags);
          if (channel.config.immediateEmailId) setMauticImmediateEmailId(channel.config.immediateEmailId.toString());
          if (channel.config.immediateCampaignId) setMauticImmediateCampaignId(channel.config.immediateCampaignId.toString());
          if (channel.config.immediateCampaignEventId) setMauticImmediateCampaignEventId(channel.config.immediateCampaignEventId.toString());
          if (channel.config.enableTrackingMerge) setMauticEnableTrackingMerge(true);
          // Existing channel has valid config — show segment/tags section
          setMauticConnectionTested(true);
        } else if (channel.type === 'twilio-sms') {
          if (channel.config.twilioAccountSid) setTwilioAccountSid(channel.config.twilioAccountSid);
          if (channel.config.twilioAuthToken) setTwilioAuthToken(channel.config.twilioAuthToken);
          if (channel.config.twilioFromNumber) setTwilioFromNumber(channel.config.twilioFromNumber);
          if (channel.config.twilioToNumbers) setTwilioToNumbers(channel.config.twilioToNumbers);
          if (channel.config.twilioMessageTemplate) setTwilioMessageTemplate(channel.config.twilioMessageTemplate);
          setTwilioConnectionTested(true);
        } else if (channel.type === 'espocrm' || channel.type === 'pbx-crm') {
          if (channel.config.espocrmHost) setEspocrmHost(channel.config.espocrmHost);
          if (channel.config.espocrmApiKey) setEspocrmApiKey(channel.config.espocrmApiKey);
          if (channel.config.espocrmTeamId) setEspocrmTeamId(channel.config.espocrmTeamId);
          if (channel.config.espocrmLeadSource) setEspocrmLeadSource(channel.config.espocrmLeadSource);
          if (channel.config.espocrmLeadStatus) setEspocrmLeadStatus(channel.config.espocrmLeadStatus);
          if (channel.config.espocrmFieldMap || channel.config.fieldMap) setEspocrmFieldMap(channel.config.espocrmFieldMap || channel.config.fieldMap);
          if (channel.config.createAccount) setEspocrmCreateAccount(true);
          if (channel.config.createOpportunity) setEspocrmCreateOpportunity(true);
          if (channel.config.opportunityDefaults) {
            const d = channel.config.opportunityDefaults;
            if (d.stage) setEspocrmOppStage(d.stage);
            if (d.probability !== undefined) setEspocrmOppProbability(d.probability.toString());
            if (d.closeDate) setEspocrmOppCloseDate(d.closeDate);
          }
          if (channel.config.opportunityFieldMap) setEspocrmOppFieldMap(channel.config.opportunityFieldMap);
          if (channel.config.optionMap) setPbxOptionMap(channel.config.optionMap);
          setEspocrmConnectionTested(true);
        }
      }
    }
  }, [channel]);

  const loadConvertkitTags = async (apiKey?: string) => {
    setLoadingConvertkit(true);
    try {
      let tags;
      // When editing an existing channel, use the channel endpoint (no API key needed)
      if (channel?.id) {
        tags = await apiClient.getChannelTags(channel.id);
      }
      // When creating a new channel, use the API key endpoint
      else if (apiKey?.trim()) {
        tags = await apiClient.getConvertkitTags(apiKey);
      } else {
        setLoadingConvertkit(false);
        return;
      }

      setConvertkitAvailableTags(tags || []);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error
          ? `Failed to load ConvertKit tags: ${err.message}`
          : 'Failed to load ConvertKit tags'
      );
    } finally {
      setLoadingConvertkit(false);
    }
  };

  // Auto-fetch tags when API key changes (with debounce to avoid too many requests)
  // Or immediately when loading an existing channel for editing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (type === 'convertkit') {
        // When editing, channel.id will exist; when creating, use the API key
        if (channel?.id || convertkitApiKey.trim()) {
          loadConvertkitTags(convertkitApiKey);
        }
      }
    }, channel?.id ? 0 : 500); // No delay when editing (we have channel.id), 500ms delay when creating (debounce API key typing)

    return () => clearTimeout(timer);
  }, [convertkitApiKey, type, channel?.id]);

  // Load Mautic segments/tags when editing an existing Mautic channel
  useEffect(() => {
    if (channel?.id && channel.type === 'mautic') {
      loadMauticDiscovery(channel.id);
    }
    if (channel?.id && channel.type === 'twilio-sms') {
      loadTwilioPhoneNumbers(channel.id);
    }
    if (channel?.id && (channel.type === 'espocrm' || channel.type === 'pbx-crm')) {
      loadEspocrmDiscovery(channel.id);
    }
  }, [channel?.id]);

  const loadMauticDiscovery = async (channelId: number) => {
    setLoadingMautic(true);
    try {
      const [segments, tags, emails, campaigns] = await Promise.all([
        apiClient.getMauticSegments(channelId),
        apiClient.getMauticTags(channelId),
        apiClient.getMauticEmails(channelId),
        apiClient.getMauticCampaigns(channelId),
      ]);
      setMauticAvailableSegments(segments);
      setMauticAvailableTags(tags);
      setMauticAvailableEmails(emails);
      setMauticAvailableCampaigns(campaigns);
      setMauticConnectionTested(true);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? `Failed to load Mautic data: ${err.message}` : 'Failed to load Mautic data');
    } finally {
      setLoadingMautic(false);
    }
  };

  const handleTestMauticConnection = async () => {
    if (!mauticHost.trim() || !mauticUsername.trim() || !mauticPassword.trim()) {
      setError('Please fill in all Mautic connection fields first');
      return;
    }

    setLoadingMautic(true);
    setError(null);

    try {
      // If editing, we can use the channel endpoint directly
      if (channel?.id) {
        await loadMauticDiscovery(channel.id);
        return;
      }

      // For new channels, we need to create it first to test, so we do a temporary test
      // by creating, testing, then loading discovery data
      const tempChannel = await apiClient.createChannel({
        name: name || 'Mautic (testing)',
        type: 'mautic',
        config: {
          mauticHost: mauticHost.trim().replace(/\/+$/, ''),
          mauticUsername: mauticUsername.trim(),
          mauticPassword: mauticPassword,
        },
      });

      try {
        await apiClient.testChannel(tempChannel.id);
        const [segments, tags, emails, campaigns] = await Promise.all([
          apiClient.getMauticSegments(tempChannel.id),
          apiClient.getMauticTags(tempChannel.id),
          apiClient.getMauticEmails(tempChannel.id),
          apiClient.getMauticCampaigns(tempChannel.id),
        ]);
        setMauticAvailableSegments(segments);
        setMauticAvailableTags(tags);
        setMauticAvailableEmails(emails);
        setMauticAvailableCampaigns(campaigns);
        setMauticConnectionTested(true);
        setError(null);
        // Clean up temp channel — we'll create the real one on submit
        await apiClient.deleteChannel(tempChannel.id);
      } catch (err) {
        // Clean up on failure too
        await apiClient.deleteChannel(tempChannel.id).catch(() => {});
        throw err;
      }
    } catch (err) {
      setMauticConnectionTested(false);
      setError(err instanceof Error ? `Mautic connection failed: ${err.message}` : 'Mautic connection failed');
    } finally {
      setLoadingMautic(false);
    }
  };

  const loadTwilioPhoneNumbers = async (channelId: number) => {
    setLoadingTwilio(true);
    try {
      const numbers = await apiClient.getTwilioPhoneNumbers(channelId);
      setTwilioAvailableNumbers(numbers);
      setTwilioConnectionTested(true);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? `Failed to load Twilio numbers: ${err.message}` : 'Failed to load Twilio numbers');
    } finally {
      setLoadingTwilio(false);
    }
  };

  const handleTestTwilioConnection = async () => {
    if (!twilioAccountSid.trim() || !twilioAuthToken.trim()) {
      setError('Please fill in Account SID and Auth Token first');
      return;
    }

    setLoadingTwilio(true);
    setError(null);

    try {
      if (channel?.id) {
        await loadTwilioPhoneNumbers(channel.id);
        return;
      }

      // For new channels, create a temp channel to test
      const tempChannel = await apiClient.createChannel({
        name: name || 'Twilio SMS (testing)',
        type: 'twilio-sms',
        config: {
          twilioAccountSid: twilioAccountSid.trim(),
          twilioAuthToken: twilioAuthToken,
        },
      });

      try {
        const numbers = await apiClient.getTwilioPhoneNumbers(tempChannel.id);
        setTwilioAvailableNumbers(numbers);
        setTwilioConnectionTested(true);
        setError(null);
        await apiClient.deleteChannel(tempChannel.id);
      } catch (err) {
        await apiClient.deleteChannel(tempChannel.id).catch(() => {});
        throw err;
      }
    } catch (err) {
      setTwilioConnectionTested(false);
      setError(err instanceof Error ? `Twilio connection failed: ${err.message}` : 'Twilio connection failed');
    } finally {
      setLoadingTwilio(false);
    }
  };

  const loadEspocrmDiscovery = async (channelId: number) => {
    setLoadingEspocrm(true);
    try {
      const [teams, fields] = await Promise.all([
        apiClient.getEspoCrmTeams(channelId),
        apiClient.getEspoCrmFields(channelId),
      ]);
      setEspocrmAvailableTeams(teams);
      setEspocrmAvailableFields(fields);
      setEspocrmConnectionTested(true);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? `Failed to load EspoCRM data: ${err.message}` : 'Failed to load EspoCRM data');
    } finally {
      setLoadingEspocrm(false);
    }
  };

  const handleTestEspocrmConnection = async () => {
    if (!espocrmHost.trim() || !espocrmApiKey.trim()) {
      setError('Please fill in EspoCRM host and API key first');
      return;
    }
    setLoadingEspocrm(true);
    setError(null);
    try {
      if (channel?.id) {
        await loadEspocrmDiscovery(channel.id);
        return;
      }
      const tempChannel = await apiClient.createChannel({
        name: name || 'EspoCRM (testing)',
        type: 'espocrm',
        config: { espocrmHost: espocrmHost.trim().replace(/\/+$/, ''), espocrmApiKey: espocrmApiKey },
      });
      try {
        await apiClient.testChannel(tempChannel.id);
        const [teams, fields] = await Promise.all([
          apiClient.getEspoCrmTeams(tempChannel.id),
          apiClient.getEspoCrmFields(tempChannel.id),
        ]);
        setEspocrmAvailableTeams(teams);
        setEspocrmAvailableFields(fields);
        setEspocrmConnectionTested(true);
        setError(null);
        await apiClient.deleteChannel(tempChannel.id);
      } catch (err) {
        await apiClient.deleteChannel(tempChannel.id).catch(() => {});
        throw err;
      }
    } catch (err) {
      setEspocrmConnectionTested(false);
      setError(err instanceof Error ? `EspoCRM connection failed: ${err.message}` : 'EspoCRM connection failed');
    } finally {
      setLoadingEspocrm(false);
    }
  };

  const handleAddEspocrmFieldMapping = () => {
    if (!espocrmNewFormField.trim() || !espocrmNewEspoField.trim()) {
      setError('Please enter both form field name and EspoCRM field');
      return;
    }
    setEspocrmFieldMap({ ...espocrmFieldMap, [espocrmNewFormField.trim()]: espocrmNewEspoField.trim() });
    setEspocrmNewFormField('');
    setEspocrmNewEspoField('');
    setError(null);
  };

  const handleAddTwilioToNumber = () => {
    const num = twilioNewToNumber.trim();
    if (!num) return;
    // Basic E.164 validation
    if (!/^\+[1-9]\d{6,14}$/.test(num)) {
      setError('Phone number must be in E.164 format: +15551234567');
      return;
    }
    if (twilioToNumbers.includes(num)) {
      setError('This number is already in the list');
      return;
    }
    setTwilioToNumbers([...twilioToNumbers, num]);
    setTwilioNewToNumber('');
    setError(null);
  };

  const handleAddMauticTag = () => {
    const tag = mauticNewTag.trim();
    if (tag && !mauticTags.includes(tag)) {
      setMauticTags([...mauticTags, tag]);
      setMauticNewTag('');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Please enter a channel name');
      return;
    }

    let config: Record<string, any> = {};

    if (type === 'slack') {
      if (!slackWebhook.trim()) {
        setError('Please enter a Slack webhook URL');
        return;
      }
      config = { slackWebhook };
    } else if (type === 'email') {
      if (!emailTo.trim()) {
        setError('Please enter at least one recipient email');
        return;
      }
      const emails = emailTo
        .split('\n')
        .map((e) => e.trim())
        .filter((e) => e);
      config = { emailTo: emails };
    } else if (type === 'pushover') {
      if (!pushoverToken.trim() || !pushoverUserKey.trim()) {
        setError('Please enter both Pushover token and user key');
        return;
      }
      const priority = parseInt(pushoverPriority, 10);
      if (priority < -2 || priority > 1) {
        setError('Priority must be between -2 and 1');
        return;
      }
      config = { pushoverToken, pushoverUserKey, priority };
    } else if (type === 'convertkit') {
      if (!convertkitApiKey.trim()) {
        setError('Please enter ConvertKit API key');
        return;
      }
      config = {
        convertkitApiKey,
      };

      if (convertkitTags.length > 0) {
        config.convertkitTags = convertkitTags.map((id) => parseInt(id, 10));
      }
    } else if (type === 'webhook') {
      if (!webhookUrl.trim()) {
        setError('Please enter a webhook URL');
        return;
      }
      config = { url: webhookUrl };

      if (webhookHeaders.trim()) {
        try {
          config.headers = JSON.parse(webhookHeaders);
        } catch {
          setError('Invalid JSON in headers field');
          return;
        }
      }

      if (webhookPayload.trim()) {
        try {
          config.payload = JSON.parse(webhookPayload);
        } catch {
          setError('Invalid JSON in payload field');
          return;
        }
      }
    } else if (type === 'mautic') {
      if (!mauticHost.trim() || !mauticUsername.trim() || !mauticPassword.trim()) {
        setError('Mautic host, username, and password are required');
        return;
      }
      config = {
        mauticHost: mauticHost.trim().replace(/\/+$/, ''),
        mauticUsername: mauticUsername.trim(),
        mauticPassword: mauticPassword,
      };
      if (mauticSegmentId) {
        config.mauticSegmentId = parseInt(mauticSegmentId, 10);
      }
      if (mauticTags.length > 0) {
        config.mauticTags = mauticTags;
      }
      if (mauticImmediateEmailId) {
        config.immediateEmailId = parseInt(mauticImmediateEmailId, 10);
      }
      if (mauticImmediateCampaignId) {
        config.immediateCampaignId = parseInt(mauticImmediateCampaignId, 10);
      }
      if (mauticImmediateCampaignEventId) {
        config.immediateCampaignEventId = parseInt(mauticImmediateCampaignEventId, 10);
      }
      if (mauticEnableTrackingMerge) {
        config.enableTrackingMerge = true;
      }
    } else if (type === 'twilio-sms') {
      if (!twilioAccountSid.trim() || !twilioAuthToken.trim()) {
        setError('Twilio Account SID and Auth Token are required');
        return;
      }
      if (!twilioFromNumber) {
        setError('Please select a From Number');
        return;
      }
      if (twilioToNumbers.length === 0) {
        setError('Please add at least one recipient phone number');
        return;
      }
      if (!twilioMessageTemplate.trim()) {
        setError('Please enter a message template');
        return;
      }
      config = {
        twilioAccountSid: twilioAccountSid.trim(),
        twilioAuthToken: twilioAuthToken,
        twilioFromNumber: twilioFromNumber,
        twilioToNumbers: twilioToNumbers,
        twilioMessageTemplate: twilioMessageTemplate,
      };
    } else if (type === 'espocrm' || type === 'pbx-crm') {
      if (!espocrmHost.trim() || !espocrmApiKey.trim()) {
        setError('EspoCRM host and API key are required');
        return;
      }
      config = {
        espocrmHost: espocrmHost.trim().replace(/\/+$/, ''),
        espocrmApiKey: espocrmApiKey,
      };
      if (espocrmTeamId) config.espocrmTeamId = espocrmTeamId;
      if (espocrmLeadSource.trim()) config.espocrmLeadSource = espocrmLeadSource.trim();
      if (espocrmLeadStatus.trim()) config.espocrmLeadStatus = espocrmLeadStatus.trim();
      if (Object.keys(espocrmFieldMap).length > 0) config.espocrmFieldMap = espocrmFieldMap;
      if (espocrmCreateAccount) {
        config.createAccount = true;
      }
      if (espocrmCreateOpportunity) {
        config.createOpportunity = true;
        config.opportunityDefaults = {
          stage: espocrmOppStage,
          probability: parseInt(espocrmOppProbability, 10) || 10,
          closeDate: espocrmOppCloseDate,
        };
        if (Object.keys(espocrmOppFieldMap).length > 0) {
          config.opportunityFieldMap = espocrmOppFieldMap;
        }
      }
      if (type === 'pbx-crm' && Object.keys(pbxOptionMap).length > 0) {
        config.optionMap = pbxOptionMap;
      }
    }

    setLoading(true);
    try {
      await onSubmit({ name, type, config, testOnCreate: testOnCreate && !channel });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      title={channel ? 'Edit Channel' : 'Create Notification Channel'}
      onClose={onClose}
      className="channel-dialog-modal"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            type="submit"
            form="channel-dialog-form"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : channel ? 'Update Channel' : 'Create Channel'}
          </button>
        </>
      }
    >
        <form id="channel-dialog-form" onSubmit={handleSubmit} className="channel-dialog-form">
          {error && (
            <div className="alert alert-error">
              <AlertIcon size={15} />
              <span>{error}</span>
            </div>
          )}

          <div className="channel-dialog-basics">
            <div className="form-group">
              <label htmlFor="name">Channel Name *</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Team Slack"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="type">Channel Type *</label>
              <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value as any)}
                disabled={loading}
              >
                <option value="slack">Slack</option>
                <option value="email">Email (AWS SES)</option>
                <option value="pushover">Pushover</option>
                <option value="convertkit">ConvertKit</option>
                <option value="mautic">Mautic</option>
                <option value="twilio-sms">Twilio SMS</option>
                <option value="espocrm">EspoCRM</option>
                <option value="pbx-crm">PBX/IVR to CRM</option>
                <option value="webhook">Webhook</option>
              </select>
            </div>
          </div>

          {type === 'slack' && (
            <div className="form-group">
              <label htmlFor="slack-webhook">Slack Webhook URL *</label>
              <input
                id="slack-webhook"
                type="password"
                value={slackWebhook}
                onChange={(e) => setSlackWebhook(e.target.value)}
                placeholder="https://hooks.slack.com/services/..."
                disabled={loading}
              />
              <small>Your Slack webhook URL for receiving form notifications</small>
            </div>
          )}

          {type === 'email' && (
            <div className="form-group">
              <label htmlFor="email-to">Recipient Emails * <small>(one per line)</small></label>
              <textarea
                id="email-to"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="support@example.com&#10;admin@example.com"
                disabled={loading}
                className="channel-dialog-textarea-sm"
              />
              <small>Email addresses that will receive form submissions (one per line)</small>
            </div>
          )}

          {type === 'pushover' && (
            <>
              <div className="form-group">
                <label htmlFor="pushover-token">Pushover Application Token *</label>
                <input
                  id="pushover-token"
                  type="password"
                  value={pushoverToken}
                  onChange={(e) => setPushoverToken(e.target.value)}
                  placeholder="Your application token"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="pushover-user">Pushover User Key *</label>
                <input
                  id="pushover-user"
                  type="password"
                  value={pushoverUserKey}
                  onChange={(e) => setPushoverUserKey(e.target.value)}
                  placeholder="Your user key"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="pushover-priority">Priority (optional)</label>
                <select
                  id="pushover-priority"
                  value={pushoverPriority}
                  onChange={(e) => setPushoverPriority(e.target.value)}
                  disabled={loading}
                >
                  <option value="-2">-2 (Very Low)</option>
                  <option value="-1">-1 (Low)</option>
                  <option value="0">0 (Normal) - Default</option>
                  <option value="1">1 (High)</option>
                </select>
                <small>Message priority level (-2 to 1, default is 0)</small>
              </div>
            </>
          )}

          {type === 'convertkit' && (
            <>
              <div className="form-group">
                <label htmlFor="convertkit-key">ConvertKit API Key *</label>
                <input
                  id="convertkit-key"
                  type="password"
                  value={convertkitApiKey}
                  onChange={(e) => setConvertkitApiKey(e.target.value)}
                  placeholder="Your API key"
                  disabled={loading}
                />
                <small>Your ConvertKit v4 API key (tags will load automatically)</small>
              </div>

              {convertkitApiKey.trim() && (
                <div className="form-group">
                  <div className="channel-dialog-label-row">
                    <label>Tags (optional)</label>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => loadConvertkitTags(convertkitApiKey)}
                      disabled={loading || loadingConvertkit}
                    >
                      {loadingConvertkit ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                  {loadingConvertkit && convertkitAvailableTags.length === 0 ? (
                    <div className="empty-state compact-empty">
                      Loading tags...
                    </div>
                  ) : convertkitAvailableTags.length > 0 ? (
                    <div className="channel-dialog-chip-list">
                      {convertkitAvailableTags.map((tag) => {
                        const isSelected = convertkitTags.includes(tag.id.toString());
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setConvertkitTags(convertkitTags.filter((id) => id !== tag.id.toString()));
                              } else {
                                setConvertkitTags([...convertkitTags, tag.id.toString()]);
                              }
                            }}
                            disabled={loading}
                            className={`channel-dialog-chip ${isSelected ? 'is-selected' : ''}`}
                          >
                            {isSelected ? 'Selected' : ''}{isSelected ? ' ' : ''}{tag.name}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="empty-state compact-empty">
                      No tags found. Check your API key or click Refresh.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {type === 'mautic' && (
            <>
              <div className="form-group">
                <label htmlFor="mautic-host">Mautic Instance URL *</label>
                <input
                  id="mautic-host"
                  type="text"
                  value={mauticHost}
                  onChange={(e) => { setMauticHost(e.target.value); setMauticConnectionTested(false); }}
                  placeholder="https://mautic.example.com"
                  disabled={loading}
                />
                <small>The base URL of your Mautic 7.x instance</small>
              </div>
              <div className="form-group">
                <label htmlFor="mautic-username">Username *</label>
                <input
                  id="mautic-username"
                  type="text"
                  value={mauticUsername}
                  onChange={(e) => { setMauticUsername(e.target.value); setMauticConnectionTested(false); }}
                  placeholder="API username"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="mautic-password">Password *</label>
                <input
                  id="mautic-password"
                  type="password"
                  value={mauticPassword}
                  onChange={(e) => { setMauticPassword(e.target.value); setMauticConnectionTested(false); }}
                  placeholder="API password"
                  disabled={loading}
                />
                <small>HTTP Basic Auth credentials. Must be enabled in Mautic API Settings.</small>
              </div>

              <div className="form-group">
                <button
                  type="button"
                  className={`btn btn-primary channel-dialog-wide-button ${mauticConnectionTested ? 'is-connected' : ''}`}
                  onClick={handleTestMauticConnection}
                  disabled={loading || loadingMautic || !mauticHost.trim() || !mauticUsername.trim() || !mauticPassword.trim()}
                >
                  {loadingMautic ? 'Testing connection...' : mauticConnectionTested ? 'Connected — Refresh' : 'Test Connection & Load Config'}
                </button>
              </div>

              {mauticConnectionTested && (
                <>
                  <div className="form-group">
                    <label htmlFor="mautic-segment">Segment (recommended)</label>
                    <select
                      id="mautic-segment"
                      value={mauticSegmentId}
                      onChange={(e) => setMauticSegmentId(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">-- No segment --</option>
                      {mauticAvailableSegments.map((seg) => (
                        <option key={seg.id} value={seg.id.toString()}>
                          {seg.name}
                        </option>
                      ))}
                    </select>
                    <small>Contact will be added to this segment, triggering any campaigns listening on it</small>
                  </div>

                  <div className="form-group">
                    <label>Tags (optional)</label>
                    <div className="channel-dialog-token-list">
                      {mauticTags.map((tag) => (
                        <span
                          key={tag}
                          className="channel-dialog-token"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => setMauticTags(mauticTags.filter((t) => t !== tag))}
                            className="channel-dialog-token-remove"
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="channel-dialog-inline-row">
                      <input
                        type="text"
                        value={mauticNewTag}
                        onChange={(e) => setMauticNewTag(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddMauticTag(); } }}
                        placeholder="Type a tag name"
                        disabled={loading}
                        list="mautic-tag-suggestions"
                        className="channel-dialog-flex"
                      />
                      <datalist id="mautic-tag-suggestions">
                        {mauticAvailableTags
                          .filter((t) => !mauticTags.includes(t.tag))
                          .map((t) => (
                            <option key={t.id} value={t.tag} />
                          ))}
                      </datalist>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleAddMauticTag}
                        disabled={loading || !mauticNewTag.trim()}
                      >
                        Add
                      </button>
                    </div>
                    <small>Tags applied to every contact created through this channel. Existing Mautic tags shown as suggestions.</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="mautic-immediate-email">Immediate Email (optional)</label>
                    <select
                      id="mautic-immediate-email"
                      value={mauticImmediateEmailId}
                      onChange={(e) => setMauticImmediateEmailId(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">-- None --</option>
                      {mauticAvailableEmails
                        .filter((e) => e.isPublished)
                        .map((email) => (
                          <option key={email.id} value={email.id.toString()}>
                            {email.name}{email.subject ? ` — "${email.subject}"` : ''}
                          </option>
                        ))}
                    </select>
                    <small>Send this email immediately when a contact is created (e.g., PDF download link, welcome email).</small>
                  </div>

                  {mauticImmediateEmailId && (
                    <div className="form-group">
                      <label htmlFor="mautic-immediate-campaign">Campaign (prevents double-send)</label>
                      <select
                        id="mautic-immediate-campaign"
                        value={mauticImmediateCampaignId}
                        onChange={(e) => {
                          setMauticImmediateCampaignId(e.target.value);
                          setMauticImmediateCampaignEventId('');
                        }}
                        disabled={loading}
                      >
                        <option value="">-- None (email sends but campaign may re-send) --</option>
                        {mauticAvailableCampaigns
                          .filter((c) => c.isPublished)
                          .map((campaign) => (
                            <option key={campaign.id} value={campaign.id.toString()}>
                              {campaign.name}
                            </option>
                          ))}
                      </select>
                      <small>If this email is part of a campaign, select it here so the campaign skips it after we send it.</small>
                    </div>
                  )}

                  {mauticImmediateEmailId && mauticImmediateCampaignId && (() => {
                    const selectedCampaign = mauticAvailableCampaigns.find((c) => c.id.toString() === mauticImmediateCampaignId);
                    const emailEvents = (selectedCampaign?.events || []).filter((e) =>
                      e.type?.includes('email')
                    );
                    return emailEvents.length > 0 ? (
                      <div className="form-group">
                        <label htmlFor="mautic-immediate-event">Campaign Event</label>
                        <select
                          id="mautic-immediate-event"
                          value={mauticImmediateCampaignEventId}
                          onChange={(e) => setMauticImmediateCampaignEventId(e.target.value)}
                          disabled={loading}
                        >
                          <option value="">-- Select the event for this email --</option>
                          {emailEvents
                            .sort((a, b) => a.order - b.order)
                            .map((event) => (
                              <option key={event.id} value={event.id.toString()}>
                                {event.name}
                              </option>
                            ))}
                        </select>
                        <small>The specific campaign event that sends this email. We mark it as done so the campaign doesn't re-send.</small>
                      </div>
                    ) : null;
                  })()}

                  <div className="form-group channel-dialog-feature">
                    <div className="channel-dialog-feature-row">
                      <input
                        type="checkbox"
                        id="mautic-tracking-merge"
                        checked={mauticEnableTrackingMerge}
                        onChange={(e) => setMauticEnableTrackingMerge(e.target.checked)}
                        disabled={loading}
                      />
                      <div>
                        <label htmlFor="mautic-tracking-merge">
                          Anonymous Contact Merge
                        </label>
                        <p>
                          When enabled, the connector will merge anonymous browsing history (page visits, lead scores) with the contact created from this form submission.
                        </p>
                        <p>
                          Requires a small frontend change. Add these two lines to your form's submit function, before the POST to Pipeline:
                        </p>
                        <pre>
{`var mtcId = (document.cookie.match(/mtc_id=(\\d+)/) || [])[1];
if (mtcId) { formData.mautic_tracking_id = mtcId; }`}
                        </pre>
                        <p className="channel-dialog-footnote">
                          Without this: contacts are created with no browsing history. With this: all prior page visits and points merge into the identified contact. If the visitor has an ad blocker or no tracking cookie, the form still works normally.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {type === 'twilio-sms' && (
            <>
              <div className="form-group">
                <label htmlFor="twilio-sid">Account SID *</label>
                <input
                  id="twilio-sid"
                  type="text"
                  value={twilioAccountSid}
                  onChange={(e) => { setTwilioAccountSid(e.target.value); setTwilioConnectionTested(false); }}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="twilio-token">Auth Token *</label>
                <input
                  id="twilio-token"
                  type="password"
                  value={twilioAuthToken}
                  onChange={(e) => { setTwilioAuthToken(e.target.value); setTwilioConnectionTested(false); }}
                  placeholder="Your Twilio Auth Token"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <button
                  type="button"
                  className={`btn btn-primary channel-dialog-wide-button ${twilioConnectionTested ? 'is-connected' : ''}`}
                  onClick={handleTestTwilioConnection}
                  disabled={loading || loadingTwilio || !twilioAccountSid.trim() || !twilioAuthToken.trim()}
                >
                  {loadingTwilio ? 'Connecting...' : twilioConnectionTested ? 'Connected — Refresh' : 'Test Connection & Load Phone Numbers'}
                </button>
              </div>

              {twilioConnectionTested && (
                <>
                  <div className="form-group">
                    <label htmlFor="twilio-from">From Number *</label>
                    <select
                      id="twilio-from"
                      value={twilioFromNumber}
                      onChange={(e) => setTwilioFromNumber(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">-- Select a phone number --</option>
                      {twilioAvailableNumbers.map((num) => (
                        <option key={num.phoneNumber} value={num.phoneNumber}>
                          {num.phoneNumber}{num.friendlyName ? ` (${num.friendlyName})` : ''}
                        </option>
                      ))}
                    </select>
                    <small>SMS-capable numbers from your Twilio account</small>
                  </div>

                  <div className="form-group">
                    <label>Send To *</label>
                    <div className="channel-dialog-token-list">
                      {twilioToNumbers.map((num) => (
                        <span
                          key={num}
                          className="channel-dialog-token"
                        >
                          {num}
                          <button
                            type="button"
                            onClick={() => setTwilioToNumbers(twilioToNumbers.filter((n) => n !== num))}
                            className="channel-dialog-token-remove"
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="channel-dialog-inline-row">
                      <input
                        type="text"
                        value={twilioNewToNumber}
                        onChange={(e) => setTwilioNewToNumber(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTwilioToNumber(); } }}
                        placeholder="+15551234567"
                        disabled={loading}
                        className="channel-dialog-flex"
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleAddTwilioToNumber}
                        disabled={loading || !twilioNewToNumber.trim()}
                      >
                        Add
                      </button>
                    </div>
                    <small>E.164 format required: +15551234567. These people receive an SMS when a form is submitted.</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="twilio-template">Message Template *</label>
                    <textarea
                      id="twilio-template"
                      value={twilioMessageTemplate}
                      onChange={(e) => setTwilioMessageTemplate(e.target.value)}
                      placeholder={'New lead: {{name}}\n{{phone}}\nScore: {{lead_score}}\ntap: tel:{{phone}}'}
                      disabled={loading}
                      className="channel-dialog-mono channel-dialog-textarea-md"
                    />
                    <small>
                      Template variables: {'{{name}}'}, {'{{email}}'}, {'{{phone}}'}, or any form field. Built-in: {'{{submissionId}}'}, {'{{formHash}}'}, {'{{ipAddress}}'}.
                    </small>
                  </div>
                </>
              )}
            </>
          )}

          {(type === 'espocrm' || type === 'pbx-crm') && (
            <>
              <div className="form-group">
                <label htmlFor="espocrm-host">EspoCRM Instance URL *</label>
                <input id="espocrm-host" type="text" value={espocrmHost}
                  onChange={(e) => { setEspocrmHost(e.target.value); setEspocrmConnectionTested(false); }}
                  placeholder="https://crm.example.com" disabled={loading} />
              </div>
              <div className="form-group">
                <label htmlFor="espocrm-apikey">API Key *</label>
                <input id="espocrm-apikey" type="password" value={espocrmApiKey}
                  onChange={(e) => { setEspocrmApiKey(e.target.value); setEspocrmConnectionTested(false); }}
                  placeholder="Your EspoCRM API key" disabled={loading} />
                <small>Create an API User in EspoCRM: Administration &gt; API Users</small>
              </div>
              <div className="form-group">
                <button type="button" onClick={handleTestEspocrmConnection}
                  disabled={loading || loadingEspocrm || !espocrmHost.trim() || !espocrmApiKey.trim()}
                  className={`btn btn-primary channel-dialog-wide-button ${espocrmConnectionTested ? 'is-connected' : ''}`}>
                  {loadingEspocrm ? 'Connecting...' : espocrmConnectionTested ? 'Connected — Refresh' : 'Test Connection & Load Config'}
                </button>
              </div>

              {espocrmConnectionTested && (
                <>
                  <div className="form-group">
                    <label htmlFor="espocrm-team">Assign to Team</label>
                    <select id="espocrm-team" value={espocrmTeamId} onChange={(e) => setEspocrmTeamId(e.target.value)} disabled={loading}>
                      <option value="">-- No team --</option>
                      {espocrmAvailableTeams.map((team) => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                    <small>New leads will be assigned to this team</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="espocrm-source">Lead Source (set on new leads only)</label>
                    <input id="espocrm-source" type="text" value={espocrmLeadSource}
                      onChange={(e) => setEspocrmLeadSource(e.target.value)}
                      placeholder="e.g., Website, HAR.com, Referral" disabled={loading} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="espocrm-status">Lead Status (set on new leads only)</label>
                    <input id="espocrm-status" type="text" value={espocrmLeadStatus}
                      onChange={(e) => setEspocrmLeadStatus(e.target.value)}
                      placeholder="New" disabled={loading} />
                  </div>

                  <div className="form-group">
                    <label>Field Mapping</label>
                    <div className="channel-dialog-note">
                      <small>Map form field names to EspoCRM Lead field names. Without mapping, common fields (email, name, phone) are auto-detected.</small>
                    </div>

                    {Object.keys(espocrmFieldMap).length > 0 && (
                      <div className="channel-dialog-mapping-list">
                        {Object.entries(espocrmFieldMap).map(([formField, espoField]) => (
                          <div key={formField} className="channel-dialog-mapping-row">
                            <input type="text" value={formField} disabled className="channel-dialog-mono" />
                            <span className="channel-dialog-arrow">&rarr;</span>
                            {espocrmAvailableFields.length > 0 ? (
                              <select value={espoField}
                                onChange={(e) => {
                                  if (e.target.value === '') {
                                    const updated = { ...espocrmFieldMap };
                                    delete updated[formField];
                                    setEspocrmFieldMap(updated);
                                  } else {
                                    setEspocrmFieldMap({ ...espocrmFieldMap, [formField]: e.target.value });
                                  }
                                }}
                                className="channel-dialog-mono">
                                <option value="">-- Remove --</option>
                                {espocrmAvailableFields.map((f) => (
                                  <option key={f.name} value={f.name}>{f.label} ({f.name})</option>
                                ))}
                              </select>
                            ) : (
                              <input type="text" value={espoField}
                                onChange={(e) => setEspocrmFieldMap({ ...espocrmFieldMap, [formField]: e.target.value })}
                                className="channel-dialog-mono" />
                            )}
                            <button type="button" onClick={() => {
                              const updated = { ...espocrmFieldMap };
                              delete updated[formField];
                              setEspocrmFieldMap(updated);
                            }} className="btn btn-sm btn-danger channel-dialog-remove">&times;</button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="channel-dialog-mapping-add-row">
                      <input type="text" value={espocrmNewFormField}
                        onChange={(e) => setEspocrmNewFormField(e.target.value)}
                        placeholder="Form field (e.g., email)" disabled={loading}
                        className="channel-dialog-mono" />
                      <span className="channel-dialog-arrow">&rarr;</span>
                      {espocrmAvailableFields.length > 0 ? (
                        <select value={espocrmNewEspoField}
                          onChange={(e) => setEspocrmNewEspoField(e.target.value)}
                          disabled={loading}
                          className="channel-dialog-mono">
                          <option value="">-- Select EspoCRM field --</option>
                          {espocrmAvailableFields.map((f) => (
                            <option key={f.name} value={f.name}>{f.label} ({f.name})</option>
                          ))}
                        </select>
                      ) : (
                        <input type="text" value={espocrmNewEspoField}
                          onChange={(e) => setEspocrmNewEspoField(e.target.value)}
                          placeholder="EspoCRM field (e.g., emailAddress)" disabled={loading}
                          className="channel-dialog-mono" />
                      )}
                    </div>
                    <button type="button" onClick={handleAddEspocrmFieldMapping}
                      disabled={loading || !espocrmNewFormField.trim() || !espocrmNewEspoField.trim()}
                      className="btn btn-sm btn-primary">
                      + Add Mapping
                    </button>
                  </div>

                  {type === 'pbx-crm' && (
                  <div className="form-group channel-dialog-feature">
                    <label>IVR Option Mapping</label>
                    <p>
                      Map IVR option values to opportunity types. New callers with a mapped option get a Contact, Account, and Opportunity. Unmapped options or returning callers just get a call note.
                    </p>

                    {Object.keys(pbxOptionMap).length > 0 && (
                      <div className="channel-dialog-mapping-list">
                        {Object.entries(pbxOptionMap).map(([key, label]) => (
                          <div key={key} className="channel-dialog-mapping-row wide-target">
                            <input type="text" value={key} disabled className="channel-dialog-mono" />
                            <span className="channel-dialog-arrow">&rarr;</span>
                            <input type="text" value={label}
                              onChange={(e) => setPbxOptionMap({ ...pbxOptionMap, [key]: e.target.value })}
                              className="channel-dialog-mono" disabled={loading} />
                            <button type="button" onClick={() => {
                              const u = { ...pbxOptionMap }; delete u[key]; setPbxOptionMap(u);
                            }} className="btn btn-sm btn-danger channel-dialog-remove">&times;</button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="channel-dialog-mapping-add-row wide-target">
                      <input type="text" value={pbxNewOptionKey}
                        onChange={(e) => setPbxNewOptionKey(e.target.value)}
                        placeholder="Option value (e.g., listing)"
                        disabled={loading} className="channel-dialog-mono" />
                      <span className="channel-dialog-arrow">&rarr;</span>
                      <input type="text" value={pbxNewOptionLabel}
                        onChange={(e) => setPbxNewOptionLabel(e.target.value)}
                        placeholder="Opportunity type (e.g., Listing Inquiry)"
                        disabled={loading} className="channel-dialog-mono" />
                    </div>
                    <button type="button" onClick={() => {
                      if (pbxNewOptionKey.trim() && pbxNewOptionLabel.trim()) {
                        setPbxOptionMap({ ...pbxOptionMap, [pbxNewOptionKey.trim()]: pbxNewOptionLabel.trim() });
                        setPbxNewOptionKey(''); setPbxNewOptionLabel('');
                      }
                    }} disabled={loading || !pbxNewOptionKey.trim() || !pbxNewOptionLabel.trim()}
                      className="btn btn-sm btn-primary">
                      + Add Option
                    </button>
                  </div>
                  )}

                  <div className="form-group channel-dialog-feature">
                    <div className="channel-dialog-feature-row">
                      <input type="checkbox" id="espocrm-create-account" checked={espocrmCreateAccount}
                        onChange={(e) => setEspocrmCreateAccount(e.target.checked)}
                        disabled={loading} />
                      <div className="channel-dialog-flex">
                        <label htmlFor="espocrm-create-account">
                          Create Account
                        </label>
                        <p>
                          Creates an Account record (e.g., "Smith Family") and links the Contact and Opportunities to it. The Account stream becomes the master timeline across all submissions. Existing contacts reuse their Account for new submissions.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="form-group channel-dialog-feature">
                    <div className="channel-dialog-feature-row">
                      <input type="checkbox" id="espocrm-create-opp" checked={espocrmCreateOpportunity}
                        onChange={(e) => setEspocrmCreateOpportunity(e.target.checked)}
                        disabled={loading} />
                      <div className="channel-dialog-flex">
                        <label htmlFor="espocrm-create-opp">
                          Auto-Create Opportunity
                        </label>
                        <p>
                          Create an Opportunity linked to the Contact for each form submission. Each submission creates a new Opportunity — same contact can have multiple deals.
                        </p>
                      </div>
                    </div>

                    {espocrmCreateOpportunity && (
                      <div className="channel-dialog-nested-fields">
                        <div className="form-group">
                          <label>Stage</label>
                          <input type="text" value={espocrmOppStage}
                            onChange={(e) => setEspocrmOppStage(e.target.value)}
                            placeholder="New Inquiry" disabled={loading} />
                        </div>
                        <div className="form-group">
                          <label>Probability (%)</label>
                          <input type="number" value={espocrmOppProbability}
                            onChange={(e) => setEspocrmOppProbability(e.target.value)}
                            placeholder="10" min="0" max="100" disabled={loading} />
                        </div>
                        <div className="form-group">
                          <label>Close Date</label>
                          <input type="text" value={espocrmOppCloseDate}
                            onChange={(e) => setEspocrmOppCloseDate(e.target.value)}
                            placeholder="+90days" disabled={loading} />
                          <small>Relative (+90days) or absolute (2026-12-31)</small>
                        </div>

                        <div className="form-group">
                          <label>Opportunity Field Mapping</label>
                          <div className="channel-dialog-note">
                            <small>Map form fields to Opportunity fields (e.g., streetAddress to description)</small>
                          </div>

                          {Object.keys(espocrmOppFieldMap).length > 0 && (
                            <div className="channel-dialog-mapping-list is-compact">
                              {Object.entries(espocrmOppFieldMap).map(([ff, of]) => (
                                <div key={ff} className="channel-dialog-mapping-row">
                                  <input type="text" value={ff} disabled className="channel-dialog-mono" />
                                  <span className="channel-dialog-arrow">&rarr;</span>
                                  <input type="text" value={of}
                                    onChange={(e) => setEspocrmOppFieldMap({ ...espocrmOppFieldMap, [ff]: e.target.value })}
                                    className="channel-dialog-mono" />
                                  <button type="button" onClick={() => {
                                    const u = { ...espocrmOppFieldMap }; delete u[ff]; setEspocrmOppFieldMap(u);
                                  }} className="btn btn-sm btn-danger channel-dialog-remove">&times;</button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="channel-dialog-mapping-add-row">
                            <input type="text" value={espocrmOppNewFormField}
                              onChange={(e) => setEspocrmOppNewFormField(e.target.value)}
                              placeholder="Form field" disabled={loading}
                              className="channel-dialog-mono" />
                            <span className="channel-dialog-arrow">&rarr;</span>
                            <input type="text" value={espocrmOppNewOppField}
                              onChange={(e) => setEspocrmOppNewOppField(e.target.value)}
                              placeholder="Opportunity field" disabled={loading}
                              className="channel-dialog-mono" />
                          </div>
                          <button type="button" onClick={() => {
                            if (espocrmOppNewFormField.trim() && espocrmOppNewOppField.trim()) {
                              setEspocrmOppFieldMap({ ...espocrmOppFieldMap, [espocrmOppNewFormField.trim()]: espocrmOppNewOppField.trim() });
                              setEspocrmOppNewFormField(''); setEspocrmOppNewOppField('');
                            }
                          }} disabled={loading || !espocrmOppNewFormField.trim() || !espocrmOppNewOppField.trim()}
                            className="btn btn-sm btn-primary channel-dialog-nested-add-button">
                            + Add Mapping
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {type === 'webhook' && (
            <>
              <div className="form-group">
                <label htmlFor="webhook-url">Webhook URL *</label>
                <input
                  id="webhook-url"
                  type="text"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://example.com/api/webhook"
                  disabled={loading}
                />
                <small>The endpoint URL to POST to when triggered</small>
              </div>
              <div className="form-group">
                <label htmlFor="webhook-headers">Headers (optional, JSON)</label>
                <textarea
                  id="webhook-headers"
                  value={webhookHeaders}
                  onChange={(e) => setWebhookHeaders(e.target.value)}
                  placeholder={'{\n  "X-API-Key": "your-secret-key"\n}'}
                  disabled={loading}
                  className="channel-dialog-mono channel-dialog-textarea-sm"
                />
                <small>Key-value pairs of HTTP headers as JSON</small>
              </div>
              <div className="form-group">
                <label htmlFor="webhook-payload">Payload Template (optional, JSON)</label>
                <textarea
                  id="webhook-payload"
                  value={webhookPayload}
                  onChange={(e) => setWebhookPayload(e.target.value)}
                  placeholder={'{\n  "ipAddress": "{{ipAddress}}",\n  "reason": "spam",\n  "score": "{{spamScore}}"\n}'}
                  disabled={loading}
                  className="channel-dialog-mono channel-dialog-textarea-lg"
                />
                <small>
                  Use template variables like {'{{ipAddress}}'}, {'{{spamScore}}'}, {'{{formHash}}'}, {'{{data}}'}, etc.
                </small>
              </div>
            </>
          )}

          {!channel && (
            <div className="form-group channel-dialog-test-option">
              <label htmlFor="test-on-create" className="checkbox-label">
              <input
                id="test-on-create"
                type="checkbox"
                checked={testOnCreate}
                onChange={(e) => setTestOnCreate(e.target.checked)}
                disabled={loading}
              />
                Test channel on creation
              </label>
            </div>
          )}
        </form>
    </ModalShell>
  );
};

export default ChannelDialog;
